import clsx from 'clsx'
import ButtonGroupToggle from 'components/Common/ButtonGroupToggle'
import type { ButtonToggleItem } from 'components/Common/ButtonGroupToggle/ButtonGroupToggleItem'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import noop from 'lib/function/noop'
import { useCallback, useContext, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import CruiseDateSelectCalendar from './CruiseWhenSelectCalendar'
import CruiseWhenSelectMonths from './CruiseWhenSelectContentMonths'
import CruiseWhenSelectHowLong from './CruiseWhenSelectHowLong'
import { getCruiseExtraFilters, getGlobalFacetFilters } from 'lib/cruises/cruiseUtils'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import useQueryParams from 'hooks/useQueryParams'
import { omitKeys } from 'lib/object/objectUtils'

// Tab buttons are localized in the component

interface Props {
  onSelect?: () => void
  onChange?: (value: App.CruiseGlobalFilters) => void
  drawerMode?: boolean
  calendarSize?: 'S' | 'M'
  initialFilters?: App.CruiseInitialFilters
}

function CruiseWhenSelectContent(props: Props) {
  const intl = useIntl()
  const { drawerMode, calendarSize, onSelect = noop, onChange = noop, initialFilters } = props
  const { checkinDate, checkoutDate } = useContext(GlobalSearchStateContext)
  const queryParams = useQueryParams()

  const tabButtons = useMemo<Array<ButtonToggleItem>>(() => [
    {
      id: 'departure-month',
      label: intl.formatMessage({
        id: 'sqSGtPswqyOlRPyJK-uS',
        defaultMessage: 'Months',
        description: 'Tab label for departure month selection in the cruises section',
      }),
    },
    {
      id: 'date-range',
      label: intl.formatMessage({
        id: 'VRdNH-oTSAHWew5ANMYx',
        defaultMessage: 'Date Range',
        description: 'Tab label for date range selection in the date selection or calendar picker',
      }),
    },
    {
      id: 'anytime',
      label: intl.formatMessage({
        id: 'weQ_0eeOhQRi1eUJFnK9',
        defaultMessage: 'Anytime',
        description: 'Tab label for anytime selection in the cruises section',
      }),
    },
  ], [intl])

  const defaultTab = useMemo(() => checkinDate && checkoutDate ? tabButtons[1] : tabButtons[0], [checkinDate, checkoutDate, tabButtons])
  const [tab, setTab] = useState<ButtonToggleItem>(defaultTab)
  const searchDispatch = useContext(GlobalSearchDispatchContext)

  const cruiseExtraFilters = getCruiseExtraFilters(queryParams)
  const globalFilters = useContext(GlobalSearchStateContext)
  const facetFilters = getGlobalFacetFilters(globalFilters)
  const [filteredCruiseDates] = useCruiseSearchFacets({
    facetTypes: ['cruise_dates'],
    ...omitKeys(
      ['departureMonths', 'departureStartDate', 'departureEndDate'],
      { ...(initialFilters || {}), ...cruiseExtraFilters, ...facetFilters },
    ),
  })

  const filteredCruiseDatesMap = useMemo(() => {
    return filteredCruiseDates.map(facet => facet.date!).filter(Boolean)
  }, [filteredCruiseDates])

  const onTabSelect = useCallback((nextTab: ButtonToggleItem) => {
    if (nextTab.id === 'anytime') {
      searchDispatch({ type: GlobalSearchStateActions.SET_FLEXIBLE_MONTH_RANGE, flexibleMonths: '' })
      searchDispatch({ type: GlobalSearchStateActions.SET_CHECKIN_DATE, date: undefined })
      searchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: undefined })
    }

    setTab(nextTab)
  }, [searchDispatch])

  return <Group direction="vertical" gap={12}>
    <Group direction="vertical" gap={12}>
      <CruiseWhenSelectHowLong onSelect={onSelect} drawerMode={drawerMode} onChange={onChange} />

      <Group direction="vertical" gap={12}>
        <Group direction="vertical" gap={12} horizontalAlign="center">
          <Heading align="center" variant="heading6">
            {intl.formatMessage({
              id: 'robonFONiUxvCBAkcPCs',
              defaultMessage: 'When would you like to sail?',
              description: 'Heading for cruise departure date selection in the cruise section',
            })}
          </Heading>
          <ButtonGroupToggle
            items={tabButtons}
            activeItem={tab}
            onItemClick={onTabSelect}
            className={clsx({ drawer: drawerMode })}
          />
        </Group>

        {tab.id === 'departure-month' && <CruiseWhenSelectMonths
          onSelect={onSelect}
          onChange={onChange}
          drawerMode={drawerMode}
          filteredCruiseDatesMap={filteredCruiseDatesMap}
          calendarSize={calendarSize}
        />}

        {tab.id === 'date-range' && <CruiseDateSelectCalendar
          type={drawerMode ? 'single' : 'dual'}
          availableMonths={filteredCruiseDatesMap}
          onChange={onChange}
        />}

        {tab.id === 'anytime' && <BodyText variant="medium" align="center" data-testid="cruise-when-select-anytime">
          {intl.formatMessage({
            id: 'vtC83Weo-fPnXbTULmIn',
            defaultMessage: 'Find all available cruises.',
            description: 'Notification message shown when anytime option is selected',
          })}
        </BodyText>}
      </Group>
    </Group>
  </Group>
}

export default CruiseWhenSelectContent
